/* eslint-disable no-console */
import axios, { AxiosError } from "axios"

import {
    GENERIC_AXIOS_TECHNICAL_DETAIL,
    GENERIC_DISPLAY_DETAIL,
    SUPPORT_REFER_HELP_DESK,
} from "./errorMessages"
import { IAppError, TErrorLevel } from "./errorTypes"
import {
    badRequestError,
    permissionDeniedError,
    pydanticValidationError,
    unrecognisedError,
} from "./recognisedErrors"
import { ensureString } from "../utils/general/generalUtils"

/**
 * Custom error class for use in the application. This class is used to
 * generate errors which are then passed to the error handling layer.
 */
export class AppError extends Error {
    displayDetail: string

    technicalDetail: string

    code: number | string

    level: TErrorLevel

    constructor(
        message: string,
        code: number | string,
        displayDetail: string,
        technicalDetail: string,
        level: TErrorLevel
    ) {
        super(message)
        this.code = code
        this.displayDetail = displayDetail
        this.technicalDetail = technicalDetail
        this.level = level
        console.error(message)
        console.error(technicalDetail)
    }
}

/**
 * Parses an axios error into an AppError.
 * @param axiosError - the axios error to be parsed
 * @returns {AppError} - the parsed error
 */
const parseAxiosError = (axiosError: AxiosError): AppError => {
    try {
        const axiosCode = axiosError?.response?.status ?? 500

        // Extract error message from axios error (some errors have a message field, some don't)
        const errorDataMsgType = axiosError.response?.data as {
            message?: string
        }
        const errorDataMessage = errorDataMsgType?.message

        // Extract error detail from axios error (some errors have a detail field, some don't)
        const errorDataDetailType = axiosError.response?.data as {
            detail?: string
        }
        const errorDataDetail = errorDataDetailType?.detail

        const whichMessage = errorDataMessage || errorDataDetail

        const msg = `${
            `${whichMessage}. ` || ""
        }To get help, please ${SUPPORT_REFER_HELP_DESK}`

        // Switch on axios error code
        switch (axiosCode) {
            case 400:
                return badRequestError(ensureString(msg, ""))
            case 403:
                return permissionDeniedError(ensureString(msg, ""))
            case 422:
                return pydanticValidationError()
            default:
                // Generic Axios error that is not a recognised error
                return new AppError(
                    axiosError?.message,
                    axiosCode,
                    ensureString(msg, GENERIC_DISPLAY_DETAIL),
                    GENERIC_AXIOS_TECHNICAL_DETAIL,
                    "warning"
                )
        }
    } catch {
        return unrecognisedError()
    }
}

/**
 * Parses an unknown error into an AppError.
 * @param error - the error to be parsed
 * @returns {AppError} - the parsed error
 */
const parseAppError = (error: unknown): AppError => {
    // If already an recognised AppError, return it
    if (error instanceof AppError) {
        return error
    }

    // If an axios error, parse it
    if (axios.isAxiosError(error)) {
        return parseAxiosError(error)
    }

    return unrecognisedError()
}

/**
 * Parses unknown error into AppError throws it. This is the preferred method
 * for processing errors in the application. Pass all errors through this
 * function to ensure they are handled in a consistent manner.
 * @param error - the error to be parsed
 * @returns {AppError} - the parsed error
 */
const parseAndThrowAppError = <T>(error: T): AppError => parseAppError(error)

/**
 * Parses unknown error into a standard error object. This is the preferred method
 * for processing errors in the application. Pass all errors through this
 * function to ensure they are handled in a consistent manner.
 * @param error - the error to be parsed
 * @returns {IAppError} - the parsed error object
 */
const parseErrorAsObj = <T>(error: T): IAppError => {
    const appError = parseAppError(error)
    return {
        message: appError.message,
        code: appError.code,
        displayDetail: appError.displayDetail,
        technicalDetail: appError.technicalDetail,
        level: appError.level,
    }
}

export { parseAndThrowAppError, parseErrorAsObj, parseAppError }
